import { z } from "zod";
/**
 * Per-track difficulty dials (structure-and-rewards phase, section 4d).
 * Authored configuration, not hardcoded behaviour: the engine reads these
 * to bias promise surfacing, widen or narrow the NEAR band, and pace the
 * nudge ladder. Law 6: dials shape authored content emphasis and pacing —
 * never a global DC/position penalty, never a gate on options. (ADR 0020,
 * 2026-09-28: a dial still sets no DC; the arc's authored structure may lower
 * one, and `repeat_patience` only paces when a stalled ask is redirected.)
 */
export const TrackDialsSchema = z.strictObject({
    /** Which subtlety of promise the ladder prefers to surface. */
    promise_subtlety_bias: z.enum(["LOUD", "PLAIN", "QUIET"]),
    /** NEAR-band width: WIDE treats missing specificity generously (partial pays), NARROW demands precision. */
    gate_tolerance_width: z.enum(["WIDE", "STANDARD", "NARROW"]),
    /** Stall turns (no LEAD_PULLED / PAYOFF_NEAR_MISS) before the ladder escalates one rung. */
    nudge_ladder_speed: z.number().int().min(1).max(6),
    /** Whether Hugh names the move that worked in plain language after a payoff. */
    mentor_explicitness: z.enum(["NAMES_THE_MOVE", "HINTS", "SILENT"]),
    /** Author-facing target for how many payoffs a session should land; used by case:check reporting, not enforcement. */
    payoff_density: z.enum(["HIGH", "MEDIUM", "LOW"]),
    /**
     * ADR 0020, idea 2: how many times the same person may be asked the same thing, each time
     * coming back short, before the engine's redirect climbs past texture. The other stuck
     * thresholds derive from this and `nudge_ladder_speed`. Pacing, never a DC.
     */
    repeat_patience: z.number().int().min(1).max(6),
});
/**
 * The authored defaults per track. CALIBRATION IS THE TUTORIAL AND MUST BE
 * UNMISTAKABLE: LOUD promises, a generous NEAR band, a fast ladder, and a
 * mentor who says out loud which move worked. Chaos runs the dials near
 * zero.
 */
export const TRACK_DIALS = {
    CALIBRATION: { promise_subtlety_bias: "LOUD", gate_tolerance_width: "WIDE", nudge_ladder_speed: 2, mentor_explicitness: "NAMES_THE_MOVE", payoff_density: "HIGH", repeat_patience: 2 },
    FRAMEWORKS: { promise_subtlety_bias: "PLAIN", gate_tolerance_width: "STANDARD", nudge_ladder_speed: 3, mentor_explicitness: "HINTS", payoff_density: "MEDIUM", repeat_patience: 2 },
    CONFLICT_MANAGEMENT: { promise_subtlety_bias: "PLAIN", gate_tolerance_width: "STANDARD", nudge_ladder_speed: 3, mentor_explicitness: "HINTS", payoff_density: "MEDIUM", repeat_patience: 3 },
    CREATIVITY: { promise_subtlety_bias: "QUIET", gate_tolerance_width: "STANDARD", nudge_ladder_speed: 4, mentor_explicitness: "HINTS", payoff_density: "MEDIUM", repeat_patience: 3 },
    CHAOS: { promise_subtlety_bias: "QUIET", gate_tolerance_width: "NARROW", nudge_ladder_speed: 6, mentor_explicitness: "SILENT", payoff_density: "LOW", repeat_patience: 3 },
};
//# sourceMappingURL=track-dials.js.map